<template>
  <div class="dutch-amount-item" :class="{ active: isActive, disabled }">
    <!-- 나눔금액 입력 화면 -->
    <template v-if="props.type === 'input'">
      <div class="dutch-amount-item__inner">
        <div class="dutch-amount-item__input-box">
          <p class="dutch-amount-item__title">{{ t('title.sharing_amount') }}</p>
          <input ref="inputRef" v-model="amount" type="text" class="dutch-amount-item__input" readonly="true" />
        </div>

        <div class="dutch-amount-item__button-group">
          <BaseButtonGroup>
            <BaseButton size="small" theme="black" @click="handleCardClick">
              {{ t('button.pay_card') }}
            </BaseButton>
            <BaseButton v-if="configStore.getIsCashPaymentUsed()" size="small" @click="handleCashClick">
              {{ t('button.pay_cash') }}
            </BaseButton>
            <BaseButton
              v-if="configStore.config.IsEasyPaymentUsed"
              size="small"
              style="background-color: #aa093f"
              @click="handleEasyClick"
            >
              {{ t('button.pay_easy') }}
            </BaseButton>
          </BaseButtonGroup>
        </div>
        <i v-if="!disabled" class="dutch-amount-item__remove" @click.stop="handleRemoveClick">
          <svg xmlns="http://www.w3.org/2000/svg" width="33.427" height="33.427" viewBox="0 0 33.427 33.427">
            <path
              d="M16.714,33.427A16.714,16.714,0,1,0,0,16.714,16.712,16.712,0,0,0,16.714,33.427Zm-5.288-22a1.56,1.56,0,0,1,2.213,0l3.069,3.069,3.069-3.069a1.565,1.565,0,1,1,2.213,2.213L18.92,16.707l3.069,3.069a1.565,1.565,0,1,1-2.213,2.213L16.707,18.92l-3.069,3.069a1.565,1.565,0,1,1-2.213-2.213l3.069-3.069-3.069-3.069A1.56,1.56,0,0,1,11.425,11.425Z"
              fill="#bfbfbf"
            />
          </svg>
        </i>
      </div>
    </template>
    <!-- 나눔금액 추가 화면 -->
    <template v-else>
      <div class="dutch-amount-item__add-box" @click="handleAddClick">
        <svg xmlns="http://www.w3.org/2000/svg" width="26.2" height="26.2" viewBox="0 0 26.2 26.2">
          <path
            id="circle-plus-solid"
            d="M13.1,26.2A13.1,13.1,0,1,0,0,13.1,13.1,13.1,0,0,0,13.1,26.2Zm-1.228-8.6V14.328H8.6a1.228,1.228,0,1,1,0-2.456h3.275V8.6a1.228,1.228,0,1,1,2.456,0v3.275H17.6a1.228,1.228,0,1,1,0,2.456H14.328V17.6a1.228,1.228,0,1,1-2.456,0Z"
            fill="#bfbfbf"
          />
        </svg>

        <span class="dutch-amount-item__text">{{ t('button.add_people') }}</span>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { formatCurrency } from '@/utils';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  // type 이 input 일 때, modelValue 는 필수로 연결 해야 함.
  interface DutchAmountItemProps {
    modelValue?: number;
    id?: string;
    type?: string;
    disabled?: boolean;
    isActive?: boolean;
  }

  /**
   * type 속성이 input 이면 금액 입력 화면이 나오고,
   * 그 외의 것들은 추가하기 화면을 보여준다.
   *
   * @type input
   */
  const props = withDefaults(defineProps<DutchAmountItemProps>(), {
    modelValue: 0,
    id: '',
    type: 'input',
    disabled: false,
  });
  // emit Event 선언
  const emits = defineEmits(['update:modelValue', 'cash', 'easy', 'card', 'remove', 'add']);

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const configStore = useConfigStore();

  const inputRef = ref();

  const amount = computed({
    get() {
      return formatCurrency(props.modelValue);
    },
    set(amount) {
      return emits('update:modelValue', formatCurrency(Number(amount)));
    },
  });

  onMounted(() => {
    focus();
  });

  const focus = () => {
    inputRef.value?.focus();
  };
  defineExpose({ focus });

  /**
   * 현금 결제 클릭 이벤트
   * @event cash
   */
  const handleCashClick = () => {
    if (props.modelValue <= 0) {
      commonStore.showDialog({
        type: 'alert',
        message: t('error.required_amount'),
      });
      return;
    }
    emits('cash', props.id);
  };

  const handleEasyClick = () => {
    if (props.modelValue <= 0) {
      commonStore.showDialog({
        type: 'alert',
        message: t('error.required_amount'),
      });
      return;
    }
    emits('easy', props.id);
  };

  /**
   * 카드결제 클릭 이벤트
   * @event card
   */
  const handleCardClick = () => {
    if (props.modelValue <= 0) {
      commonStore.showDialog({
        type: 'alert',
        message: t('error.required_amount'),
      });
      return;
    }
    emits('card', props.id);
  };

  /**
   * 오브젝트 제거 클릭 이벤트
   * @event remove
   */
  const handleRemoveClick = () => {
    emits('remove', props.id);
  };

  /**
   * 오브젝트 추가 클릭 이벤트
   * @event add
   */
  const handleAddClick = () => {
    emits('add');
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .dutch-amount-item {
    position: relative;
    height: 100%;
    box-shadow: vars.$base-box-shadow-black;
    padding: 0.8955vw;
    border-radius: 0.3731vw;
    border: 0.2985vw solid #fff;
    // 입력 값 터치 시 포커스 효과
    &.active {
      border: 0.2985vw solid var(--border-color-secondary);
      .dutch-amount-item__input-box:after {
        bottom: -0.2239vw;
        height: 0.2239vw;
        background: var(--border-color-secondary);
      }
    }
    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    .dutch-amount-item__inner {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
    }
    .dutch-amount-item__input-box {
      position: relative;
      padding-bottom: 0.597vw;
      margin-bottom: 0.597vw;
      &:after {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 0.0746vw;
        content: '';
        background: var(--border-color-primary);
      }
    }
    .dutch-amount-item__title {
      font-size: 1.194vw;
      font-weight: 500;
      margin-bottom: 0.8955vw;
      @media (max-width: vars.$sm) {
        font-size: 12px;
      }
    }
    .dutch-amount-item__input {
      width: 100%;
      border: none;
      outline: 0;
      font-size: 1.6418vw;
      font-weight: 700;
      color: #000;
      @media (max-width: vars.$sm) {
        font-size: 12px;
      }
      &::placeholder {
        font-size: 1.6418vw;
        color: var(--text-secondary);
      }
    }
    .dutch-amount-item__button-group {
      display: flex;
      .BaseButton {
        height: 2.9851vw;
        @media (max-width: vars.$md) {
          height: 24px;
        }
        span {
          font-size: 1.2687vw;
        }
      }
      .BaseButton + .BaseButton {
        margin-left: 0.4478vw;
      }
    }
    .dutch-amount-item__remove {
      position: absolute;
      top: 0;
      right: 0;
      padding: 5px;
      svg {
        width: 1.9403vw;
        height: 1.9403vw;
        @media (max-width: vars.$md) {
          width: 20px;
          height: 20px;
        }
      }
    }

    .dutch-amount-item__add-box {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      svg {
        width: 1.9403vw;
        path {
          fill: #000;
        }
      }
      .dutch-amount-item__text {
        font-size: 1.6418vw;
        font-weight: 500;
        margin-left: 0.4478vw;
      }
    }
  }
</style>
